import type { DealFormData } from '@openx/types/deal';
import type { ExchangeSegmentPartnerPlatformShareCreateParams } from '@openx/types/gqlTypes';
import type { SegmentPartnerShare } from './platformFeeValidation.utils';

export type PlatformShareInput = DealFormData['segment_partner_platform_share'];

export const parseShareValue = (share: string | number | null | undefined): number => {
  if (share == null || share === '') return 0;

  const numericValue = Number(share);

  return Number.isFinite(numericValue) ? numericValue : 0;
};

export const percentageToDecimal = (percentage: number): string => {
  const decimal = (percentage / 100).toFixed(3);
  const normalized = decimal.replace(/\.?0+$/, '');

  return normalized || '0';
};

export const decimalToPercentage = (decimal: string | number | null | undefined): string | null => {
  if (!decimal) return null;

  const numericValue = Number(decimal);

  if (!Number.isFinite(numericValue)) return null;

  const value = numericValue * 100;

  return parseFloat(value.toFixed(1)).toString();
};

export const transformPlatformShareToPercent = (shares: PlatformShareInput): PlatformShareInput => {
  if (!shares || !Array.isArray(shares)) return null;

  if (shares.length === 0) return [];

  return shares.map((entry: SegmentPartnerShare | null) => {
    if (!entry?.partner_id) return null;

    return {
      partner_id: entry.partner_id,
      share: decimalToPercentage(entry.share) ?? '',
    };
  });
};

export const transformPlatformShareToDecimal = (
  shares: PlatformShareInput
): Array<ExchangeSegmentPartnerPlatformShareCreateParams | null> | null => {
  if (!shares || !Array.isArray(shares)) return null;

  if (shares.length === 0) return [];

  let hasNonZeroShare = false;

  const transformed: Array<ExchangeSegmentPartnerPlatformShareCreateParams | null> = shares.map(
    (entry: SegmentPartnerShare | null) => {
      if (!entry?.partner_id) return null;

      const percentageValue = parseShareValue(entry.share);
      const decimalShare = percentageToDecimal(percentageValue);

      if (decimalShare !== '0') {
        hasNonZeroShare = true;
      }

      return {
        partner_id: entry.partner_id,
        share: decimalShare,
      };
    }
  );

  return hasNonZeroShare ? transformed : null;
};
